// ─── Module inventory — « Plans & Emplacements » ──────────────────
// Navigation dans les lieux de l'espace, niveau par niveau, en vue 2D (plan) ou
// Liste, avec la pastille du nombre d'objets rangés (agrégé sur TOUS les
// inventaires de l'espace). Levé de MoPCal (PlansEmplacementsScreen) : mêmes
// gestes, mêmes boutons d'en-tête, même 💾.
//
// Trois modes, exclusifs, tous pilotés depuis l'en-tête :
//   • consultation : 🗺/☰ · tap tuile = voir le contenu · double tap = entrer
//   • 🌳 structure : créer / renommer / déplacer / dupliquer / supprimer un lieu
//   • ✏️ plan      : grille, murs, placement — brouillon local, 💾 pour écrire
//
// `mode='readonly'` (MoPCal) : consultation seule, aucun bouton d'édition.
import React, { useEffect, useMemo, useRef, useState } from 'react';
// Les motifs de « non trouvé » vivent dans une ref : ils accompagnent le
// pointage jusqu'à la validation, sans re-rendre la liste à chaque frappe.
import {
  View, Text, TextInput, TouchableOpacity, ScrollView, Modal, StyleSheet, Image,
} from 'react-native';
import {
  useUI, useT, useEspace, useAuth, PageHeader, EmptyState, LigneSwipe, SelecteurEmoji, confirmerSuppression,
  useMenuOngletActif, GesteRetourEcran, Feuille,
} from '@mop/core';
import {
  ecouterInventairesAccessibles, ecouterItems, lireEmplacementsUneFois,
  modifierItem, noeudParId, parentDe, idsDescendants, aplatirArbre, cheminDe,
  articlesDe, photosArticle, nouvelId,
} from './services/inventaires';
import {
  ecouterPlansAccessibles, creerPlan, renommerPlan, modifierEmojiPlan,
  modifierArbrePlan, supprimerPlan,
} from './services/plansData';
import {
  compterDirect, compterTotaux, cheminIdsEmp, disposerPlan,
  majPlanCols, majPlanRows, poserMur, placerPlan, redimPlan, basculerCellZone,
} from './services/plan';
import { parseCodeEmplacement } from './services/qr';
import { makeStylesPlans } from './stylesPlans';
import SelecteurPlan from './ui/SelecteurPlan';
import PlanGrid from './ui/PlanGrid';
import ScannerCodeBarres from './ui/ScannerCodeBarres';
import FicheElement from './FicheElement';
import ArbreContenu from './ArbreContenu';
import EditeurPlan2D from './EditeurPlan2D';
import FeuilleLieu from './FeuilleLieu';
import useDonneesPlans from './hooks/useDonneesPlans';

const clone = (x) => JSON.parse(JSON.stringify(x || []));
const HIT = { top: 8, bottom: 8, left: 8, right: 8 };
const BROSSES = [['fin', '▏', 'inventaire.planCloison'], ['epais', '▍', 'inventaire.planMur'], ['porte', '🚪', 'inventaire.planPorte'], ['fenetre', '🪟', 'inventaire.planFenetre']];

export default function EcranPlans({ mode = 'edit', ongletCle = 'Plans' }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStylesPlans(UI), [UI]);
  const { tenantRangement, espaceActif } = useEspace();
  const { user } = useAuth();
  const edit = mode === 'edit';

  // ── Données + dérivés : hooks/useDonneesPlans (plans, inventaires, comptages). ──
  const {
    plans, plansPrets, planSelId, setPSI, planCourant, emplacements, planEff,
    itemsParInv, err, setErr, creerPlanUI, renommerPlanUI, supprimerPlanUI, quota,
    compteDe, elementsDe, elementsSousLieu,
  } = useDonneesPlans({ edit });

  const [vue, setVue]           = useState('plan');   // 'plan' | 'liste'
  const [masquerVides, setMV]   = useState(true);     // 🌿 cacher les emplacements vides
  const [cheminIds, setChemin]  = useState([]);       // pile d'ids (racine → courant)
  const [contenuInline, setCI]  = useState(null);     // lieu dont on montre le contenu
  const [voirNiveau, setNiveau] = useState(false);    // afficher le contenu de TOUT le niveau courant
  const [contPlies, setContPlies] = useState(() => new Set());  // sous-lieux repliés dans l'arbre de contenu
  const [lieuxListeOuv, setLLO]   = useState(() => new Set());  // lieux dépliés dans la vue LISTE
  const [elemListeOuv, setELO]    = useState(() => new Set());  // éléments dépliés dans la vue LISTE
  const [recherche, setRech]    = useState('');       // recherche article / élément / lieu
  const tapTuile  = useRef({ t: 0, id: null });
  const motifsRef = useRef({});                       // { [articleId]: motif }
  const toggleCont = (id) => setContPlies(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // Édition du plan du niveau courant (brouillon local, écrit au 💾).
  const [editPlan, setEditPlan] = useState(false);
  const [planDraft, setDraft]   = useState(null);
  const [planBase, setBase]     = useState('');
  const [murMode, setMurMode]   = useState(false);
  const [brosse, setBrosse]     = useState('fin');
  const [formeMode, setForme]   = useState(false);
  const [planSel, setPlanSel]   = useState(null);

  // Édition de la structure des lieux.
  const [structMode, setStruct] = useState(false);
  const [fEmp, setFEmp]         = useState(null);     // formulaire lieu | null
  const [pickParent, setPick]   = useState(false);

  // Scan (QR d'emplacement ou code-barres produit) et session de comptage.
  const [scan, setScan]     = useState(false);
  const [info, setInfo]     = useState('');
  const [session, setSess]  = useState(false);        // « faire l'inventaire »
  const [pointes, setPnt]   = useState({});           // { [articleId]: 'present'|'absent' }
  const [motifArt, setMotif] = useState(null);        // article dont on saisit le motif
  const [motifTxt, setMTxt] = useState('');
  const [fiche, setFiche]   = useState(null);         // { item, articleIndex } — fiche info article
  const [selLieux, setSel]  = useState(() => new Set()); // sélection multiple d'emplacements (appui long)

  // Appui long sur l'onglet → menu déroulant « Configuration » (accès à la
  // configuration de la structure des lieux, sans bouton en en-tête).
  useMenuOngletActif(ongletCle, [
    { label: t('plans.configuration'), icone: '🌳', onPress: () => setStruct(true) },
  ], edit);

  const toggleLieuListe = (id) => setLLO(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleElemListe = (cle) => setELO(p => { const n = new Set(p); n.has(cle) ? n.delete(cle) : n.add(cle); return n; });

  // Arbre de contenu : composant récursif ArbreContenu (props partagées).
  const propsArbre = {
    emplacements, compteDe, elementsDe, masquerVides, contPlies, toggleCont,
    entrerLieu, session, pointes, pointer,
    onMotif: (a) => { setMotif(a); setMTxt(motifsRef.current[a.id] || ''); },
    onFiche: (item, articleIndex) => setFiche({ item, articleIndex }),
  };

  // Recherche : lieux + éléments + articles (avec leur emplacement).
  const q = recherche.trim().toLowerCase();
  const resultats = useMemo(() => {
    if (!q) return [];
    const out = [];
    aplatirArbre(emplacements).forEach(o => {
      if ((o.nom || '').toLowerCase().includes(q)) out.push({ type: 'lieu', icone: o.emoji || '📍', nom: o.nom, sous: cheminDe(emplacements, o.id), empId: o.id });
    });
    Object.values(itemsParInv).forEach(its => (its || []).forEach(it => {
      const arts = articlesDe(it);
      arts.forEach(a => {
        if (!a.emplacementId) return;
        const cible = (it.nom || '').toLowerCase().includes(q) || (a.nom || '').toLowerCase().includes(q) || String(a.codeBarres || '').includes(q);
        if (cible) out.push({ type: 'objet', icone: it.emoji || '📦', nom: a.nom || it.nom, sous: cheminDe(emplacements, a.emplacementId), empId: a.emplacementId });
      });
    }));
    return out.slice(0, 40);
  }, [q, emplacements, itemsParInv]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Session « faire l'inventaire » ───────────────────────────────
  // On pointe article par article (présent / absent + motif), puis on VALIDE :
  // chaque élément touché reçoit sa date de comptage, et chaque article son
  // dernier pointage. Rien n'est écrit tant qu'on n'a pas validé.
  const nbPointes = Object.keys(pointes).length;
  // Re-toucher le même état DÉ-pointe : on retire la clé (sinon elle resterait
  // avec `undefined` et fausserait le compteur).
  const pointer = (artId, etat) => setPnt(p => {
    const n = { ...p };
    if (n[artId] === etat) delete n[artId]; else n[artId] = etat;
    return n;
  });

  async function validerSession() {
    const maintenant = new Date().toISOString();
    const parItem = new Map();                      // itemId → { invId, item }
    Object.values(itemsParInv).forEach(its => (its || []).forEach(it => {
      if (articlesDe(it).some(a => pointes[a.id])) parItem.set(it.id, it);
    }));
    for (const [invId, its] of Object.entries(itemsParInv)) {
      for (const it of (its || [])) {
        if (!parItem.has(it.id)) continue;
        // Format MoPCal : on APPEND une entrée dans `histInv`
        // { date, etat, deplace, empAvant, empApres, raison }.
        const arts = articlesDe(it).map(a => (pointes[a.id]
          ? { ...a, histInv: [...(Array.isArray(a.histInv) ? a.histInv : []),
              { date: maintenant, etat: pointes[a.id], deplace: false, raison: motifsRef.current[a.id] || '' }] }
          : a));
        try { await modifierItem(invId, it.id, { articles: arts, compteLe: maintenant }); }
        catch (e) { setErr(e?.message || String(e)); }
      }
    }
    setInfo(t('plans.comptageEnregistre', { n: nbPointes }));
    setPnt({}); motifsRef.current = {}; setSess(false);
  }

  // Le QR d'un emplacement mène directement à son plan ; un code-barres
  // retrouve l'objet et affiche où il est rangé.
  function apresScan(texte) {
    setScan(false);
    const emp = parseCodeEmplacement(texte);
    if (emp?.emplacementId) {
      const n = noeudParId(emplacements, emp.emplacementId);
      if (!n) { setInfo(t('plans.qrLieuInexistant')); return; }
      setInfo(`📍 ${cheminDe(emplacements, emp.emplacementId)}`);
      entrerLieu(emp.emplacementId);
      setCI(emp.emplacementId);
      return;
    }
    const code = String(texte || '').trim();
    for (const its of Object.values(itemsParInv)) {
      for (const it of (its || [])) {
        const a = articlesDe(it).find(x => String(x.codeBarres || '') === code);
        if (a) {
          if (a.emplacementId) { setInfo(`${it.nom} → ${cheminDe(emplacements, a.emplacementId)}`); entrerLieu(a.emplacementId); setCI(a.emplacementId); }
          else setInfo(t('plans.objetRangeNullePart', { nom: it.nom }));
          return;
        }
      }
    }
    setInfo(t('plans.codeAucunObjet', { code }));
  }

  // ── Navigation ───────────────────────────────────────────────────
  const src       = editPlan && planDraft ? planDraft : emplacements;
  const courantId = cheminIds[cheminIds.length - 1] || null;
  const racine    = !courantId;
  const parent    = courantId
    ? noeudParId(src, courantId)
    : { id: null, nom: planCourant?.nom || t('inventaire.plan'), emoji: planCourant?.emoji || '🗺️',
        enfants: src, planCols: 3, planRows: 0, planMurs: [] };
  const enfants   = parent?.enfants || [];

  const descendre  = (n) => { setChemin(c => c.concat([n.id])); setCI(null); setNiveau(false); };
  const remonter   = () => { setChemin(c => c.slice(0, -1)); setCI(null); setNiveau(false); };
  const allerNiveau = (i) => { setChemin(c => c.slice(0, i)); setCI(null); setNiveau(false); };
  const entrerLieu = (id) => { setVue('plan'); setCI(null); setNiveau(false); setChemin(cheminIdsEmp(emplacements, id)); };
  // Tap sur le NIVEAU courant (fil d'Ariane) → voir/masquer le contenu de tout
  // ce niveau (sous-lieux → éléments → articles), en récursif.
  const basculerNiveau = () => { setCI(null); setSel(new Set()); setNiveau(v => !v); };

  // Tap simple = contenu sous le plan (et EFFACE la multi-sélection, comme
  // MoPCal) ; double tap = entrer ; appui long = sélection multiple.
  const onTapTuileNav = (n) => {
    const now = Date.now();
    if (tapTuile.current.id === n.id && now - tapTuile.current.t < 300) {
      tapTuile.current = { t: 0, id: null };
      descendre(n);
    } else {
      tapTuile.current = { t: now, id: n.id };
      setSel(new Set());                       // un tap simple efface la sélection multiple
      setNiveau(false);                        // …et le contenu « niveau entier »
      setCI(p => (p === n.id ? null : n.id));
    }
  };

  // ── Édition du plan (brouillon → 💾) ─────────────────────────────
  const majDraft   = (fn) => setDraft(d => { const t2 = clone(d || emplacements); fn(t2); return t2; });
  const entrerEdit = () => {
    setEditPlan(true); setDraft(clone(emplacements)); setBase(JSON.stringify(emplacements));
    setMurMode(false); setForme(false); setPlanSel(null); setVue('plan'); setCI(null);
  };
  const sortirEdit = () => { setEditPlan(false); setDraft(null); setMurMode(false); setForme(false); setPlanSel(null); };
  const modifie    = editPlan && !!planDraft && JSON.stringify(planDraft) !== planBase;
  const sauverPlan = async () => {
    if (!planDraft) return;
    try { await modifierArbrePlan(planEff, planDraft); setBase(JSON.stringify(planDraft)); }
    catch (e) { setErr(e?.message || String(e)); }
  };
  // Retire un sous-lieu du plan (garde ses dimensions) → il retourne « à placer ».
  const retirerDuPlan = (id) => majDraft(tr => {
    const c = noeudParId(tr, id);
    if (c) { if (c.cells) delete c.cells; c.cell = { w: (c.cell && c.cell.w) || 1, h: (c.cell && c.cell.h) || 1 }; }
  });
  const onTapTuilePlan = (n) => {
    const now = Date.now();
    if (tapTuile.current.id === n.id && now - tapTuile.current.t < 300) {
      tapTuile.current = { t: 0, id: null };
      retirerDuPlan(n.id); setPlanSel(x => (x === n.id ? null : x));
    } else {
      tapTuile.current = { t: now, id: n.id };
      setPlanSel(x => (x === n.id ? null : n.id));
    }
  };

  const cols     = Math.max(1, parent?.planCols || 3);
  const dispEdit = parent ? disposerPlan(parent, false) : { rows: 2, nonPlaces: [] };
  const rows     = dispEdit.rows;
  const nonPlaces = dispEdit.nonPlaces;

  // ── Édition de la structure ──────────────────────────────────────
  const ouvrirFormEmp = (parentId, id) => {
    const n = id ? noeudParId(src, id) : null;
    let parentActuel = parentId || null;
    if (id) { const p = parentDe(src, id); parentActuel = p && p.parent ? p.parent.id : null; }
    setFEmp({
      id: id || null, parentActuel, nouveauParentId: parentActuel,
      nom: n ? n.nom : '', emoji: n ? (n.emoji || '📍') : '📍',
      nomInitial: n ? n.nom : '', emojiInitial: n ? (n.emoji || '📍') : '📍',
    });
    setPick(false);
  };
  const fermerFormEmp = () => { setFEmp(null); setPick(false); };
  const empModifie = !!fEmp && !!fEmp.nom.trim() && (
    !fEmp.id || fEmp.nom.trim() !== fEmp.nomInitial || fEmp.emoji !== fEmp.emojiInitial
    || (fEmp.nouveauParentId || null) !== (fEmp.parentActuel || null)
  );

  const enregistrerEmp = async () => {
    if (!fEmp || !empModifie) return;
    const nom = fEmp.nom.trim();
    // En édition de plan, on écrit dans le BROUILLON (sinon on écraserait/serait
    // écrasé par le plan non encore validé).
    const enDraft = editPlan && !!planDraft;
    const t2 = clone(enDraft ? planDraft : emplacements);
    const nvParent = fEmp.nouveauParentId || null;
    if (fEmp.id) {
      const n = noeudParId(t2, fEmp.id);
      if (n) {
        n.nom = nom; n.emoji = fEmp.emoji || '📍';
        if (nvParent !== (fEmp.parentActuel || null)) {
          const desc = new Set(idsDescendants(t2, n.id));
          if (!nvParent || !desc.has(nvParent)) {          // jamais dans sa propre descendance
            const pc = parentDe(t2, n.id);
            if (pc) { const i = pc.liste.findIndex(x => x.id === n.id); if (i >= 0) pc.liste.splice(i, 1); }
            if (nvParent) { const np = noeudParId(t2, nvParent); if (np) { np.enfants = np.enfants || []; np.enfants.push(n); } else t2.push(n); }
            else t2.push(n);
          }
        }
      }
    } else {
      const node = { id: nouvelId('e'), nom, emoji: fEmp.emoji || '📍', enfants: [] };
      if (nvParent) { const p = noeudParId(t2, nvParent); if (p) { p.enfants = p.enfants || []; p.enfants.push(node); } else t2.push(node); }
      else t2.push(node);
    }
    if (enDraft) { setDraft(t2); fermerFormEmp(); return; }
    try { await modifierArbrePlan(planEff, t2); fermerFormEmp(); }
    catch (e) { setErr(e?.message || String(e)); }
  };

  const supprimerEmp = (id) => {
    const n = noeudParId(emplacements, id);
    confirmerSuppression(
      t('plans.supprLieuTitre'),
      t('plans.supprLieuMsg', { nom: n?.nom || '' }),
      async () => {
        try {
          const ids = new Set(idsDescendants(emplacements, id));
          for (const [invId, its] of Object.entries(itemsParInv)) {
            for (const it of (its || [])) {
              if (!(it.articles || []).some(a => ids.has(a.emplacementId))) continue;
              const liste = (it.articles || []).map(a => ids.has(a.emplacementId) ? { ...a, emplacementId: null } : a);
              try { await modifierItem(invId, it.id, { articles: liste }); } catch (_) {}
            }
          }
          const t2 = clone(emplacements);
          const p = parentDe(t2, id);
          if (p) { const i = p.liste.findIndex(x => x.id === id); if (i >= 0) p.liste.splice(i, 1); }
          else { const i = t2.findIndex(x => x.id === id); if (i >= 0) t2.splice(i, 1); }
          await modifierArbrePlan(planEff, t2);
        } catch (e) { setErr(e?.message || String(e)); }
      },
    );
  };

  // Duplique un lieu (et sa sous-structure) au même niveau, avec de NOUVEAUX ids
  // → le double est vierge d'objets (ceux-ci restent rangés dans l'original).
  const cloneNouveauxIds = (n) => ({ ...n, id: nouvelId('e'), enfants: (n.enfants || []).map(cloneNouveauxIds) });
  const dupliquerEmp = async (id) => {
    try {
      const t2 = clone(emplacements);
      const p = parentDe(t2, id);
      const liste = p ? p.liste : t2;
      const i = liste.findIndex(x => x.id === id);
      if (i < 0) return;
      const copie = cloneNouveauxIds(liste[i]);
      copie.nom = `${liste[i].nom} ${t('plans.suffixeCopie')}`;
      liste.splice(i + 1, 0, copie);
      await modifierArbrePlan(planEff, t2);
    } catch (e) { setErr(e?.message || String(e)); }
  };

  // Fil d'Ariane.
  const crumbs = [{ id: '__root__', nom: planCourant?.nom || t('inventaire.plan') }]
    .concat(cheminIds.map(id => { const nd = noeudParId(emplacements, id); return { id, nom: nd ? nd.nom : '—' }; }));

  const empPlat = useMemo(() => aplatirArbre(emplacements), [emplacements]);
  const exclus  = fEmp?.id ? new Set(idsDescendants(emplacements, fEmp.id)) : new Set();

  // ── Aucun plan sélectionné ──────────────────────────────────────
  // On peut avoir PLUSIEURS plans (Maison, Garage…). Tant qu'aucun n'est choisi,
  // on montre le sélecteur (pour créer/choisir) + un état vide. Un membre sans
  // plan partagé voit un message adapté (pas de blocage sur « espace »).
  if (!planCourant) {
    return (
      <View style={s.page}>
        <PageHeader titre={t('plans.titrePlansEmplacements')} iconeTitre="🗺️" />
        {edit && (
          <SelecteurPlan plans={plans} planSelId={planSelId} onSelect={setPSI}
            peutEditer={!!espaceActif} peutCreer={quota.peutCreer}
            messageQuota={t('jetons.creationBloquee', { n: quota.possedes, max: quota.max })}
            onCreer={creerPlanUI} onRenommer={renommerPlanUI} onSupprimer={supprimerPlanUI} />
        )}
        <EmptyState icon="🗺️" texte={plans.length ? t('plans.choisisPlan') : t('plans.aucunPlan')}
          aide={espaceActif ? t('plans.aidePlanCreer')
                : t('plans.aidePlanPartage')} />
      </View>
    );
  }

  // Retour courant (bouton ‹ ET geste bord gauche). Désactivé en édition de
  // plan 2D (le doigt y sert à dessiner) et derrière une modale ouverte.
  const retourCourant = editPlan ? sortirEdit : (structMode ? () => setStruct(false) : (racine ? undefined : remonter));
  const modaleOuverte = scan || !!fiche || !!fEmp || !!motifArt;
  const gesteRetourActif = !!retourCourant && !modaleOuverte && !editPlan;

  return (
    <GesteRetourEcran actif={gesteRetourActif} onRetour={retourCourant}>
    <View style={s.page}>
      <PageHeader
        titre={racine ? t('plans.titrePlansEmplacements') : (parent?.nom || '')}
        iconeTitre={racine ? '🗺️' : (parent?.emoji || '📍')}
        onRetour={editPlan ? sortirEdit : (structMode ? () => setStruct(false) : (racine ? undefined : remonter))}>
        {editPlan ? (
          <>
            <TouchableOpacity onPress={sauverPlan} disabled={!modifie} hitSlop={HIT} accessibilityLabel={t('commun.enregistrer')}>
              <Text style={[s.hIc, !modifie && { opacity: 0.35 }]}>💾</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={sortirEdit} hitSlop={HIT} accessibilityLabel={t('tuto.terminer')}>
              <Text style={s.hIc}>✓</Text>
            </TouchableOpacity>
          </>
        ) : structMode ? (
          <>
            <TouchableOpacity onPress={() => ouvrirFormEmp(courantId, null)} hitSlop={HIT} accessibilityLabel={t('lieux.nouveauTitre')}>
              <Text style={s.hIc}>＋</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => setStruct(false)} hitSlop={HIT} accessibilityLabel={t('tuto.terminer')}>
              <Text style={s.hIc}>✓</Text>
            </TouchableOpacity>
          </>
        ) : (
          <>
            <TouchableOpacity onPress={() => { setInfo(''); setScan(true); }} hitSlop={HIT} accessibilityLabel={t('plans.scannerAcc')}>
              <Text style={s.hIc}>📷</Text>
            </TouchableOpacity>
            {/* La session de comptage se lance depuis la RACINE : elle couvre tout
                l'espace, et le pointage se poursuit en naviguant dans les lieux. */}
            {edit && racine && (
              <TouchableOpacity onPress={() => { setSess(v => !v); setPnt({}); motifsRef.current = {}; }}
                hitSlop={HIT} accessibilityLabel={t('inventaire.faireInventaire')}>
                <Text style={[s.hIc, session && s.hIcOn]}>📋</Text>
              </TouchableOpacity>
            )}
            {edit && enfants.length > 0 && (
              <TouchableOpacity onPress={entrerEdit} hitSlop={HIT} accessibilityLabel={t('plans.editerPlan')}>
                <Text style={s.hIc}>✏️</Text>
              </TouchableOpacity>
            )}
            <View style={s.seg}>
              <TouchableOpacity style={[s.segBtn, vue === 'plan' && s.segBtnOn]} onPress={() => setVue('plan')}>
                <Text style={[s.segTxt, vue === 'plan' && s.segTxtOn]}>🗺</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[s.segBtn, vue === 'liste' && s.segBtnOn]} onPress={() => setVue('liste')}>
                <Text style={[s.segTxt, vue === 'liste' && s.segTxtOn]}>☰</Text>
              </TouchableOpacity>
            </View>
          </>
        )}
      </PageHeader>

      {/* Sélecteur de PLAN (Maison, Garage…) — au niveau racine, hors édition. */}
      {!editPlan && !structMode && racine && (
        <SelecteurPlan plans={plans} planSelId={planSelId}
          onSelect={(id) => { setPSI(id); setChemin([]); setCI(null); setNiveau(false); }}
          peutEditer={edit && !!espaceActif} peutCreer={quota.peutCreer}
          messageQuota={t('jetons.creationBloquee', { n: quota.possedes, max: quota.max })}
          onCreer={creerPlanUI} onRenommer={renommerPlanUI} onSupprimer={supprimerPlanUI} />
      )}

      {!!err && (
        <TouchableOpacity style={s.err} onPress={() => setErr('')}>
          <Text style={s.errTxt}>⚠️ {err}</Text>
        </TouchableOpacity>
      )}
      {!!info && (
        <TouchableOpacity style={s.infoB} onPress={() => setInfo('')}>
          <Text style={s.infoTxt}>{info}</Text>
        </TouchableOpacity>
      )}

      {/* Bandeau de session : persistant pendant qu'on navigue de lieu en lieu. */}
      {session && (
        <View style={s.sess}>
          <Text style={s.sessTxt}>
            📋 {t('plans.comptageEnCours', { n: nbPointes })}
          </Text>
          <TouchableOpacity style={[s.sessBtn, !nbPointes && { opacity: 0.4 }]} disabled={!nbPointes} onPress={validerSession}>
            <Text style={s.sessBtnTxt}>{t('commun.valider')}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={s.sessAnnul} onPress={() => { setSess(false); setPnt({}); motifsRef.current = {}; }}>
            <Text style={s.sessAnnulTxt}>{t('commun.annuler')}</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Fil d'Ariane */}
      {cheminIds.length > 0 && (
        <View style={s.crumbs}>
          {crumbs.map((c, i) => {
            const courant = i === crumbs.length - 1;
            return (
              <View key={c.id + i} style={s.crumbItem}>
                {i > 0 && <Text style={s.crumbSep}>›</Text>}
                {/* Niveau courant : tap = voir/masquer le contenu de tout le niveau. */}
                <TouchableOpacity onPress={() => (courant ? basculerNiveau() : allerNiveau(i))}>
                  <Text style={[s.crumbTxt, courant && s.crumbCur]} numberOfLines={1}>
                    {c.nom}{courant ? ` ${voirNiveau ? '▾' : '▸'}` : ''}
                  </Text>
                </TouchableOpacity>
              </View>
            );
          })}
        </View>
      )}

      {/* ── ÉDITION DU PLAN ──────────────────────────────────────── */}
      {editPlan ? (
        <EditeurPlan2D parent={parent} courantId={courantId} cols={cols} rows={rows}
          nonPlaces={nonPlaces} majDraft={majDraft}
          murMode={murMode} setMurMode={setMurMode} brosse={brosse} setBrosse={setBrosse}
          formeMode={formeMode} setForme={setForme} planSel={planSel} setPlanSel={setPlanSel}
          onTapTuilePlan={onTapTuilePlan} ouvrirFormEmp={ouvrirFormEmp} compteDe={compteDe} />

      /* ── ÉDITION DE LA STRUCTURE ───────────────────────────────── */
      ) : structMode ? (
        <ScrollView contentContainerStyle={s.corps} keyboardShouldPersistTaps="handled">
          <Text style={s.editTitre}>{racine ? t('plans.lieuxEspace') : t('plans.contenuDe', { nom: parent?.nom })}</Text>
          {enfants.length === 0 && (
            <EmptyState icon="📍" texte={t('plans.aucunLieuNiveau')} aide={t('plans.aucunLieuNiveauAide')} />
          )}
          {enfants.map(n => (
            <LigneSwipe key={n.id}
              onSupprimer={() => supprimerEmp(n.id)}
              onEditer={() => ouvrirFormEmp(null, n.id)}
              onDupliquer={() => dupliquerEmp(n.id)}>
              <TouchableOpacity style={s.ligne} activeOpacity={0.7} onPress={() => descendre(n)}
                onLongPress={() => ouvrirFormEmp(null, n.id)} delayLongPress={450}>
                <Text style={s.ligneEmoji}>{n.emoji || '📍'}</Text>
                <View style={{ flex: 1 }}>
                  <Text style={s.ligneNom} numberOfLines={1}>{n.nom}</Text>
                  <Text style={s.ligneSous}>
                    {t('plans.nSousLieux', { n: (n.enfants || []).length })} · {t('plans.nObjets', { n: compteDe(n.id) })}
                  </Text>
                </View>
                <Text style={s.chev}>›</Text>
              </TouchableOpacity>
            </LigneSwipe>
          ))}
          <Text style={s.aide}>{t('plans.aideSwipeLignes')}</Text>
          <View style={{ height: 30 }} />
        </ScrollView>

      /* ── CONSULTATION ──────────────────────────────────────────── */
      ) : (
        <ScrollView contentContainerStyle={s.corps}>
          {/* Recherche article / élément / lieu (comme MoPCal). */}
          <View style={s.rechRow}>
            <Text style={s.rechIc}>🔍</Text>
            <TextInput style={s.rechInput} value={recherche} onChangeText={setRech}
              placeholder={t('plans.recherchePlaceholder')} placeholderTextColor={UI.faint} autoCorrect={false} />
            {!!recherche && <TouchableOpacity onPress={() => setRech('')} hitSlop={HIT}><Text style={s.rechIc}>✕</Text></TouchableOpacity>}
            {/* 🌿 masquer les emplacements vides — dans la barre, comme l'inventaire. */}
            <TouchableOpacity style={[s.mvBtn, masquerVides && s.mvBtnOn]} onPress={() => setMV(v => !v)}
              accessibilityLabel={t('plans.masquerVidesAcc')}>
              <Text style={s.mvIc}>🌿</Text>
            </TouchableOpacity>
          </View>
          {masquerVides && <Text style={s.hint}>{t('plans.videsMasques')}</Text>}
          {!!q && (
            <View style={s.contenu}>
              {resultats.length === 0
                ? <Text style={s.aide}>{t('plans.aucunResultat')}</Text>
                : resultats.map((r, i) => (
                    <TouchableOpacity key={i} style={s.resRow} onPress={() => { setRech(''); entrerLieu(r.empId); setCI(r.empId); }}>
                      <Text style={{ fontSize: 16 }}>{r.icone}</Text>
                      <View style={{ flex: 1 }}>
                        <Text style={s.resNom} numberOfLines={1}>{r.nom}</Text>
                        <Text style={s.resSous} numberOfLines={1}>{r.type === 'lieu' ? t('plans.typeLieu') : `📍 ${r.sous}`}</Text>
                      </View>
                      <Text style={s.chev}>›</Text>
                    </TouchableOpacity>
                  ))}
            </View>
          )}

          {enfants.length === 0 ? (
            <EmptyState icon="📍" texte={t('plans.aucunLieuIci')}
              aide={edit ? t('plans.aucunLieuIciAideEdit') : t('plans.rienAAfficher')} />
          ) : vue === 'plan' ? (
            <PlanGrid
              parent={parent} flux
              // 🌿 : on cache les tuiles des lieux vides (0 objet), murs conservés.
              cacherIds={masquerVides ? (parent.enfants || []).filter(n => compteDe(n.id) === 0).map(n => n.id) : []}
              onTapTuile={onTapTuileNav}
              onLongPressTuile={(n) => { setCI(null); setSel(s2 => { const x = new Set(s2); x.has(n.id) ? x.delete(n.id) : x.add(n.id); return x; }); }}
              onTapPastille={(id) => { setSel(new Set()); setNiveau(false); setCI(p => (p === id ? null : id)); }}
              onLongPressPastille={(id) => { setCI(null); setSel(s2 => { const x = new Set(s2); x.has(id) ? x.delete(id) : x.add(id); return x; }); }}
              // Surligne le lieu dont on montre le contenu + la sélection multiple.
              cibleIds={[...(contenuInline ? [contenuInline] : []), ...selLieux]}
              compteDe={compteDe}
            />
          ) : (
            // Vue LISTE : chaque lieu est un ACCORDÉON qui déploie TOUS ses
            // éléments/articles (récursif, sous-lieux inclus) comme l'inventaire —
            // l'article porte son 📍 lieu, sans montrer la hiérarchie des sous-lieux.
            enfants.filter(n => !masquerVides || compteDe(n.id) > 0).map(n => {
              const ouv = lieuxListeOuv.has(n.id);
              const total = compteDe(n.id);
              const nbSous = (n.enfants || []).length;
              const elems = ouv ? elementsSousLieu(n.id) : [];
              return (
                <View key={n.id}>
                  <TouchableOpacity style={[s.ligne, { marginBottom: 8 }]} activeOpacity={0.7}
                    onPress={() => toggleLieuListe(n.id)}
                    onLongPress={() => descendre(n)} delayLongPress={450}>
                    <Text style={s.ligneEmoji}>{n.emoji || '📍'}</Text>
                    <View style={{ flex: 1 }}>
                      <Text style={s.ligneNom} numberOfLines={1}>{n.nom}</Text>
                      <Text style={s.ligneSous}>
                        {t('plans.nObjets', { n: total })}{nbSous ? ` · ${t('plans.nSousLieux', { n: nbSous })}` : ''}
                      </Text>
                    </View>
                    <Text style={s.chev}>{ouv ? '▾' : '›'}</Text>
                  </TouchableOpacity>
                  {ouv && (elems.length === 0
                    ? <Text style={[s.aide, { textAlign: 'left', marginLeft: 14, marginBottom: 8 }]}>{t('plans.aucunObjetIci')}</Text>
                    : elems.map(g => {
                        const cle = `${n.id}:${g.id}`;
                        const ouvE = elemListeOuv.has(cle);
                        const totalE = g.arts.reduce((s2, x) => s2 + x.qte, 0);
                        return (
                          <View key={g.id}>
                            <TouchableOpacity style={[s.contElem, { marginLeft: 18 }]} activeOpacity={0.7} onPress={() => toggleElemListe(cle)}>
                              <Text style={s.contChev}>{ouvE ? '▾' : '▸'}</Text>
                              {g.arts[0]?.photo
                                ? <Image source={{ uri: g.arts[0].photo.url }} style={s.contArtPhoto} />
                                : <Text style={{ fontSize: 15 }}>{g.emoji}</Text>}
                              <Text style={s.contElemNom} numberOfLines={1}>{g.nom}</Text>
                              <Text style={s.contElemQte}>{totalE}</Text>
                            </TouchableOpacity>
                            {ouvE && g.arts.map(a => {
                              const idx = articlesDe(g.item).findIndex(x => x.id === a.id);
                              return (
                                <TouchableOpacity key={a.id} style={[s.contArt, { marginLeft: 44 }]} activeOpacity={0.7}
                                  onPress={() => setFiche({ item: g.item, articleIndex: Math.max(0, idx) })}>
                                  {a.photo ? <Image source={{ uri: a.photo.url }} style={s.contArtPhoto} /> : <Text style={{ fontSize: 13 }}>🏷️</Text>}
                                  <View style={{ flex: 1 }}>
                                    <Text style={s.contArtNom} numberOfLines={1}>{a.nom}</Text>
                                    <Text style={s.llArtLieu} numberOfLines={1}>📍 {noeudParId(emplacements, a.emplacementId)?.nom || '—'}</Text>
                                  </View>
                                  <Text style={s.contArtQte}>{a.unique ? '1' : a.qte}</Text>
                                </TouchableOpacity>
                              );
                            })}
                          </View>
                        );
                      }))}
                </View>
              );
            })
          )}

          {vue === 'plan' && enfants.length > 0 && (
            <Text style={s.aide}>
              {t('plans.aideTuiles')}
            </Text>
          )}
          {selLieux.size > 0 && (
            <View style={s.selBarre}>
              <Text style={s.selTxt}>{t('plans.nLieux', { n: selLieux.size })}</Text>
              <TouchableOpacity onPress={() => setSel(new Set())}><Text style={s.selAnnul}>{t('commun.effacer')}</Text></TouchableOpacity>
            </View>
          )}

          {/* Contenu des lieux affichés : ARBRE récursif (lieu → sous-lieux →
              éléments), avec compteurs et accordéons — comme MoPCal. En
              sélection multiple, on affiche le contenu de CHAQUE lieu ; sinon le
              lieu du tap simple. */}
          {!q && (selLieux.size > 0 || !!contenuInline) && (
            <View style={s.contenu}>
              {(selLieux.size > 0 ? [...selLieux] : [contenuInline]).map(id => (
                <View key={id} style={{ marginBottom: 10 }}>
                  <ArbreContenu {...propsArbre} nodeId={id} prof={0} racine
                    onFermer={selLieux.size > 0
                      ? () => setSel(s2 => { const x = new Set(s2); x.delete(id); return x; })
                      : () => setCI(null)} />
                </View>
              ))}
            </View>
          )}

          {/* Contenu de TOUT le niveau courant (tap sur le fil d'Ariane). */}
          {voirNiveau && !q && selLieux.size === 0 && !contenuInline && (
            <View style={s.contenu}>
              {courantId
                ? <ArbreContenu {...propsArbre} nodeId={courantId} prof={0} racine onFermer={() => setNiveau(false)} />
                : (enfants.filter(n => !masquerVides || compteDe(n.id) > 0).length === 0
                    ? <Text style={[s.aide, { textAlign: 'left', marginLeft: 4 }]}>{t('plans.aucunContenuNiveau')}</Text>
                    : enfants.filter(n => !masquerVides || compteDe(n.id) > 0)
                        .map(n => <View key={n.id} style={{ marginBottom: 10 }}><ArbreContenu {...propsArbre} nodeId={n.id} prof={0} racine onFermer={() => setNiveau(false)} /></View>))}
            </View>
          )}
          <View style={{ height: 30 }} />
        </ScrollView>
      )}

      {/* Fiche info d'un article (lecture seule) depuis l'arbre de contenu. */}
      <FicheElement visible={!!fiche} item={fiche?.item} articleIndex={fiche?.articleIndex || 0}
        emplacements={emplacements} compteDe={compteDe} onFermer={() => setFiche(null)} />

      {/* Scan plein écran (QR de lieu OU code-barres produit). */}
      <ScannerCodeBarres visible={scan} mode="mixte" onClose={() => setScan(false)} onScan={apresScan} />

      {/* Motif de « non trouvé » — petite feuille, ouverte seule. */}
      <Modal visible={!!motifArt} animationType="fade" transparent onRequestClose={() => setMotif(null)}>
        <View style={s.fondCentre}>
          <View style={s.motif}>
            <Text style={s.motifTitre}>{t('plans.motifNonTrouve', { nom: motifArt?.nom })}</Text>
            <Text style={s.aide}>{t('plans.motifAide')}</Text>
            <TextInput style={s.input} value={motifTxt} onChangeText={setMTxt} autoFocus
              placeholder={t('plans.phMotif')} placeholderTextColor={UI.faint} />
            <View style={s.motifBtns}>
              <TouchableOpacity onPress={() => setMotif(null)}><Text style={s.motifPasser}>{t('plans.passer')}</Text></TouchableOpacity>
              <TouchableOpacity style={s.motifOk}
                onPress={() => { if (motifArt) motifsRef.current[motifArt.id] = motifTxt.trim(); setMotif(null); }}>
                <Text style={s.motifOkTxt}>{t('commun.enregistrer')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Feuille « lieu » (création / édition) — composant dédié. */}
      <FeuilleLieu fEmp={fEmp} setFEmp={setFEmp} pickParent={pickParent} setPick={setPick}
        empModifie={empModifie} enregistrerEmp={enregistrerEmp} fermerFormEmp={fermerFormEmp}
        empPlat={empPlat} exclus={exclus} emplacements={emplacements}
        planTitre={planCourant?.nom} />
    </View>
    </GesteRetourEcran>
  );
}
